import { memo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Trans, useTranslation } from "react-i18next";
import { BodyCopy } from "../../components/BodyCopy";
import {
  AuraLayout,
  Button,
  CashSpecialIcon,
  Close,
  GradientText,
  Layout,
  SuccessIcon,
  Title,
} from "lola-framework-ui-test";
import { SupportV2Icon } from "lola-framework-ui-test/src/icons";
import { useStore } from "../../store/globalState";
import { M2M_ROUTES, TITLE_COLOR } from "../../constants";
import { LegalModal } from "../../components/legal/LegalModal";
import { Popup } from "../../components/Popup";
import { PayoutInfo } from "../../components/PayoutInfo";
import { m2mMockReceipt } from "../../gallery/m2mMock";
import { amountFormatter, formatLabeledAmount } from "../../utils";
import { LimitInfoIcon, StatusCircleIcon } from "../../assets/icons";
import {
  resolveStatusToneColor,
  toStatusPresentation,
  type StatusTone,
} from "../../utils/statusPresentation";
import "../../styles/legal.style.css";
import { ScrollFadeIndicator } from "../../components/ScrollFadeIndicator";
import { Navbar } from "../../components/Navbar";


const statusIcon = (tone: StatusTone, color: string) => {
  switch (tone) {
    case "completed":
      return <SuccessIcon size={10} colors={[color, color]} />;
    case "failed":
    case "expired":
      return <Close size={10} colors={[color, color]} />;
    case "initial":
    case "progress":
    default:
      return <StatusCircleIcon size={10} color={color} />;
  }
};

const formatDateTime = (isoDate: string): string => {
  const date = new Date(isoDate);
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  const year = String(date.getFullYear());
  return `${day}/${month}/${year}\n${date.toLocaleTimeString()}`;
};

const money = (amount: number | undefined, currency: string): string =>
  formatLabeledAmount(amount ?? 0, currency);

/**
 * M2M receipt (gallery). Static completed operation + LegalModal cancellation
 * policy. Cancel confirm is inspectable chrome — no cancelOperation API.
 */
export const ReceiptSummaryPage = memo(() => {
  const { t } = useTranslation("m2m");
  const { theme } = useStore();
  const navigate = useNavigate();

  const colorConfig = theme.whiteView;
  const {
    background,
    backgroundBtn,
    textColorBtn,
    bodyCopy,
    dropzoneColors,
    title,
    footerColor,
  } = colorConfig;
  const [showTransactionPolicyModal, setShowTransactionPolicyModal] =
    useState(false);
  const [showConfirm, setShowConfirm] = useState(false);

  const operation = m2mMockReceipt;
  const { tone, labelKey } = toStatusPresentation(operation.status);
  const statusColor = resolveStatusToneColor(tone);
  const [sendInt, sendDec] = amountFormatter(
    operation.sendAmount,
    operation.sourceCurrency
  ).split(".");

  const summaryItems: {
    label: string;
    value: string;
    isBold?: boolean;
    statusColor?: string;
    icon?: React.ReactNode;
  }[] = [
    {
      label: t("receiptSummary.fields.status"),
      value: t(labelKey, { ns: "translation" }),
      isBold: true,
      statusColor,
      icon: statusIcon(tone, statusColor),
    },
    {
      label: t("receiptSummary.fields.transfer.amount"),
      value: money(operation.sendAmount, operation.sourceCurrency),
    },
    {
      label: t("receiptSummary.fields.transaction.fee"),
      value: money(operation.transactionFee, operation.sourceCurrency),
    },
    {
      label: t("receiptSummary.fields.exchange.rate"),
      value: `${amountFormatter(operation.exchangeRate, operation.destinationCurrency, {
        minimumFractionDigits: 4,
        maximumFractionDigits: 4,
      })} ${operation.destinationCurrency}/${operation.sourceCurrency}`,
    },
    {
      label: t("receiptSummary.fields.total.payment"),
      value: money(operation.totalCost, operation.sourceCurrency),
    },
    {
      label: t("receiptSummary.fields.receiving"),
      value: money(operation.receiveAmount, operation.destinationCurrency),
    },
    {
      label: t("receiptSummary.fields.funding.fee"),
      value: money(operation.fundingFee, operation.sourceCurrency),
    },
    {
      label: t("receiptSummary.fields.tax.fee"),
      value: money(operation.taxFee, operation.sourceCurrency),
    },
    {
      label: t("receiptSummary.fields.receiver"),
      value: operation.receiverName,
    },
    {
      label: t("receiptSummary.fields.trasnaction.date"),
      value: formatDateTime(operation.createdAt),
    },
  ];

  return (
    <>
      <AuraLayout
        colorConfig={colorConfig}
        className="receipt-summary-section white-view-background flavor-a"
      >
        <Layout.Header>
          <Navbar
            title={t("transfer.summary.title")}
            align="center"
            onBackClick={() => navigate(M2M_ROUTES.TRANSFER_A)}
            icon={<SupportV2Icon colors={dropzoneColors} />}
            onIconClick={() => navigate(M2M_ROUTES.SUPPORT_V2)}
          />
        </Layout.Header>
        <Layout.Content isOverflowauto>
          <ScrollFadeIndicator color={bodyCopy} />
          <section className="receipt-summary-container">
            <section className="receipt-summary-wrapper">
              <section className="summary-section gradient-card">
                <section className="summary-title">
                  <BodyCopy textColor={bodyCopy}>
                    {t("receiptSummary.youveTransferred")}
                  </BodyCopy>
                  <GradientText as="h1">
                    ${sendInt}.<label>{sendDec ?? "00"}</label>{" "}
                    {operation.sourceCurrency}
                  </GradientText>
                  <BodyCopy textColor={bodyCopy}>
                    <b>{t("receiptSummary.transactionId")}</b>
                  </BodyCopy>
                  <BodyCopy textColor={bodyCopy}>
                    {operation.operationId.slice(0, 8).toUpperCase()}
                  </BodyCopy>
                </section>
                <section className="summary-info">
                  {summaryItems.map(
                    (
                      { label, value, isBold, statusColor: color, icon },
                      index
                    ) => (
                      <SummaryInfoItem
                        key={index}
                        status={label}
                        value={value}
                        isBold={isBold}
                        statusColor={color}
                        icon={icon}
                      />
                    )
                  )}
                </section>
                {operation.cancellation?.allowed && (
                  <button
                    type="button"
                    className="m2m-cancel-operation-btn"
                    style={{ color: bodyCopy }}
                    onClick={() => setShowConfirm(true)}
                  >
                    {t("receiptSummary.cancelTransfer")}
                  </button>
                )}
              </section>

              <PayoutInfo
                title={t("receiptSummary.transferAgain")}
                subtitle=""
                icon={<CashSpecialIcon size={30} />}
                background={backgroundBtn}
                color={textColorBtn}
                onChange={() => navigate(M2M_ROUTES.TRANSFER_A)}
              />
            </section>
            <section className="receipt-summary-disclaimers">
              <BodyCopy className="footer-text" textColor={footerColor}>
                <Trans i18nKey="receiptSummary.transferNote" ns="m2m" />
              </BodyCopy>
              <BodyCopy className="footer-text">
                <GradientText
                  as="button"
                  type="button"
                  className="receipt-cancel-policy"
                  textColor={title}
                  onClick={() => setShowTransactionPolicyModal(true)}
                >
                  {t("receiptSummary.cancelationPolicy")}
                </GradientText>
              </BodyCopy>
            </section>
          </section>
        </Layout.Content>
      </AuraLayout>
      <LegalModal
        doc="transactionPolicy"
        visible={showTransactionPolicyModal}
        onClose={() => setShowTransactionPolicyModal(false)}
      />
      <Popup visible={showConfirm} onClose={() => setShowConfirm(false)} showClose={false}>
        <section className="cancel-confirm-slot">
          <LimitInfoIcon
            className="cancel-confirm-icon"
            size={60}
            colors={[TITLE_COLOR, TITLE_COLOR]}
          />
          <Title
            title={t("receiptSummary.cancelConfirmTitle")}
            align="center"
            color={TITLE_COLOR}
          />
          <BodyCopy textColor={bodyCopy}>
            {t(
              operation.cancellation?.mode === "void"
                ? "receiptSummary.cancelConfirmMessageVoid"
                : "receiptSummary.cancelConfirmMessageRequest"
            )}
          </BodyCopy>
          <div className="cancel-confirm-actions">
            <Button
              size="large"
              variant="outline"
              background={backgroundBtn}
              color={TITLE_COLOR}
              style={{ backgroundColor: background }}
              onClick={() => setShowConfirm(false)}
            >
              {t("receiptSummary.cancelConfirmYes")}
            </Button>
            <Button
              size="large"
              background={backgroundBtn}
              color={textColorBtn}
              onClick={() => setShowConfirm(false)}
            >
              {t("receiptSummary.cancelConfirmNo")}
            </Button>
          </div>
        </section>
      </Popup>
    </>
  );
});

interface SummaryInfoItemProps {
  isBold?: boolean;
  statusColor?: string;
  status: string;
  value: string | React.ReactNode;
  icon?: React.ReactNode;
}

const SummaryInfoItem: React.FC<SummaryInfoItemProps> = ({
  isBold,
  status,
  value,
  statusColor,
  icon,
}) => (
  <section className={`summary-info-item ${isBold ? "bold" : ""} `}>
    <p>{status}</p>
    <p
      className={icon ? "summary-info-status" : undefined}
      style={{
        ...(statusColor ? { color: statusColor } : undefined),
        whiteSpace: "pre-line",
        textAlign: "right",
      }}
    >
      {icon}
      {value}
    </p>
  </section>
);
